import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A muddy 1x1 tile crossed by a timber duckboard walkway — the plank paths
// laid over the mud between trenches. Two runners carry a ladder of slats and
// run the FULL tile along Z (length exactly 1 tile), so adjacent segments abut
// into a continuous path; dark puddles pool in the mud beside it. Authored
// footprint-native, centred on the origin, base at y = 0.
const MUD = "#5d4a33";        // churned mud tile
const MUD_WET = "#463623";    // standing water in the puddles
const PLANK = "#c9a36a";      // duckboard slats
const PLANK_DARK = "#a9885a"; // weathered slats + runners

const RUN_X = 0.11;   // runner offset from the centreline
const SLAT_W = 0.34;  // slat length across the walkway
const SLAT_Y = 0.1;   // slat resting height on the runners

function slat(z: number, dark = false): Part {
  return { geometry: new THREE.BoxGeometry(SLAT_W, 0.03, 0.09), color: dark ? PLANK_DARK : PLANK,
    position: [0, SLAT_Y, z] };
}

function puddle(x: number, z: number, r: number): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, 0.012, 8), color: MUD_WET,
    position: [x, 0.082, z], scale: [1.25, 1, 1] };
}

export const duckboardPath = defineModel({
  id: "duckboard-path",
  batchId: "wwi",
  author: "Fable 5",
  name: "Duckboard Path",
  tags: ["wwi", "scenery", "trench", "path", "mud", "duckboard"],
  summary: "A muddy tile crossed by a timber duckboard walkway, with puddles pooling beside the planks.",
  description:
    "One tile of trench-country ground: churned mud with a timber duckboard laid " +
    "over it — plank slats on two runners — running the full tile along Z so " +
    "segments abut into a continuous walkway. Dark puddles pool in the mud on " +
    "either side.",
  footprint: 0.55,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // The mud tile itself, a thin ground slab covering the full 1x1 cell.
      { geometry: new THREE.BoxGeometry(1, 0.08, 1), color: MUD, position: [0, 0.04, 0] },
      // Two runners carrying the slats, the full tile long so segments abut.
      { geometry: new THREE.BoxGeometry(0.05, 0.05, 1), color: PLANK_DARK, position: [-RUN_X, 0.08, 0] },
      { geometry: new THREE.BoxGeometry(0.05, 0.05, 1), color: PLANK_DARK, position: [RUN_X, 0.08, 0] },
      // The ladder of slats, alternating weathering, up to the tile edges.
      slat(-0.44),
      slat(-0.315, true),
      slat(-0.19),
      slat(-0.065, true),
      slat(0.065),
      slat(0.19, true),
      slat(0.315),
      slat(0.44, true),
      // Puddles pooling in the mud beside the walkway.
      puddle(-0.32, -0.24, 0.12),
      puddle(-0.35, 0.28, 0.08),
      puddle(0.33, 0.12, 0.13),
      // Churned mud clods breaking the flat tile.
      { geometry: new THREE.IcosahedronGeometry(0.07, 0), color: MUD, position: [0.3, 0.09, -0.32], scale: [1.2, 0.55, 1] },
      { geometry: new THREE.IcosahedronGeometry(0.055, 0), color: MUD, position: [-0.33, 0.09, 0.02], scale: [1.1, 0.5, 1] },
    ];
  },
  animations: [],
});
